/**
 * Benchmark API service — content quality evaluation endpoints.
 */
import { apiClient } from './apiClient';
import { useTenantStore } from '@/stores/useTenantStore';

const getTenantId = () => useTenantStore.getState().currentTenant;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BenchmarkSubScore {
  name: string;
  score: number;
  weight: number;
  detail: string;
}

export interface BenchmarkCategory {
  category: string;
  score: number;
  weight: number;
  sub_scores: BenchmarkSubScore[];
  findings: string[];
  method: 'code' | 'llm' | 'hybrid';
}

export interface AILexicalInfo {
  total_flags: number;
  flags_per_1000_words: number;
  category_counts: Record<string, number>;
  burstiness_score: number;
  passive_voice_ratio: number;
  flagged_words: Record<string, number>;
}

export interface BenchmarkResult {
  content_id: string;
  keyword: string;
  model_used: string;
  tenant: string;
  composite_score: number;
  grade: string;
  categories: BenchmarkCategory[];
  ai_lexical: AILexicalInfo | null;
  word_count: number;
  evaluated_at: string;
  duration_ms: number;
  llm_calls: number;
  errors: string[];
}

export interface BenchmarkListItem {
  content_id: string;
  keyword: string;
  composite_score: number;
  grade: string;
  word_count: number;
  model_used: string;
  evaluated_at: string;
  ai_flags: number;
  status: string;
  tenant: string;
}

export interface BenchmarkListResponse {
  items: BenchmarkListItem[];
  total: number;
  has_benchmark: number;
}

export interface BenchmarkAggregate {
  avg_composite: number;
  total_articles: number;
  benchmarked_articles: number;
  avg_generation_time: number;
  avg_ai_flags: number;
  grade_distribution: Record<string, number>;
  category_averages: Record<string, number>;
}

export interface RunBenchmarkResponse {
  success: boolean;
  result: BenchmarkResult | null;
  error: string;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function fetchBenchmarkResults(
  limit = 20,
  offset = 0,
  tenantIds?: string[],
): Promise<BenchmarkListResponse> {
  const params: Record<string, string | number> = { limit, offset };
  if (tenantIds && tenantIds.length > 0) {
    params.tenant_ids = tenantIds.join(',');
  } else {
    params.tenant_id = getTenantId() ?? '';
  }
  const { data } = await apiClient.get<BenchmarkListResponse>(
    '/benchmark/results',
    { params },
  );
  return data;
}

export async function fetchTenantList(): Promise<string[]> {
  const { data } = await apiClient.get<Array<{ website: string }>>(
    '/tenant-settings/',
  );
  return data.map((t) => t.website);
}

export async function fetchBenchmarkAggregate(
  tenantIds: string[],
  period = '30d',
): Promise<BenchmarkAggregate> {
  const params: Record<string, string> = { period };
  if (tenantIds.length > 0) {
    params.tenant_ids = tenantIds.join(',');
  }
  const { data } = await apiClient.get<BenchmarkAggregate>(
    '/benchmark/aggregate',
    { params },
  );
  return data;
}

export async function fetchBenchmarkDetail(contentId: string): Promise<BenchmarkResult> {
  const { data } = await apiClient.get<BenchmarkResult>(
    `/benchmark/results/${contentId}`,
    { params: { tenant_id: getTenantId() } },
  );
  return data;
}

export async function runBenchmark(
  contentId: string,
  codeOnly = true,
): Promise<RunBenchmarkResponse> {
  const { data } = await apiClient.post<RunBenchmarkResponse>(
    `/benchmark/run/${contentId}`,
    { code_only: codeOnly },
    { params: { tenant_id: getTenantId() }, timeout: 120_000 },
  );
  return data;
}
